// Copyright 2016 Sorama B.V.

import { BindingEngine, computedFrom, inject } from "aurelia-framework";
import {
    AudioStreamProperties,
    CaptureDevice,
    FrontEnd,
    MeasurementHealthIssue,
    MeasurementHealthIssueType,
    OrderMode,
    Point3f,
    Region,
    Resolution,
    StreamBeamformingProjection,
    StreamBeamformingProjectionProperties,
    StreamHologram,
    StreamHologramProperties,
    StreamSpectrum,
    StreamSpectrumProperties,
    VideoStreamProperties,
} from "datatypes/SoramaLocalApi";
import { Status } from "datatypes/Status";
import { StreamMeasurementServiceError } from "errors/StreamMeasurementServiceError";
import { PromiseExtensions } from "extensions/PromiseExtensions";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { ChannelSelectionUsage } from "services/ChannelSelection/ChannelSelectionUsage";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { ConnectedMeasurementDeviceSetup } from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IHardwareReserver, IReservableHardwareService } from "services/Hardware/IReservableHardwareService";
import { IMeasurementManagementService } from "services/Measurement/IMeasurementManagementService";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IStorageService, StorageTypes } from "services/Storage/IStorageService";

// Internals
import { SoundStreamPreviewService } from "./Internal/SoundStreamPreviewService";
import { StreamMeasurementService } from "./Internal/StreamMeasurementService";
import { VideoStreamPreviewService } from "./Internal/VideoStreamPreviewService";

class PositionedCaptureDevice {
    captureDevice: CaptureDevice;
    position: Point3f;
}

@inject(
    "IReservableHardwareService<FrontEnd,string>",
    "IReservableHardwareService<CaptureDevice,string>",
    StreamMeasurementService,
    SoundStreamPreviewService,
    VideoStreamPreviewService,
    IStorageService,
    IChannelSelectionPresetService,
    INotificationService,
    BindingEngine
)
export class MeasurementManagementService implements IMeasurementManagementService, IHardwareReserver<string> {
    //#region Injected Services
    private readonly frontEndService: IReservableHardwareService<FrontEnd, string>;
    private readonly captureDeviceService: IReservableHardwareService<CaptureDevice, string>;
    private readonly soundStreamPreviewService: SoundStreamPreviewService;
    private readonly videoStreamPreviewService: VideoStreamPreviewService;
    private readonly streamMeasurementService: StreamMeasurementService;
    private readonly storageService: IStorageService;
    private readonly channelSelectionService: IChannelSelectionPresetService;
    private readonly notificationService: INotificationService;
    private readonly bindingEngine: BindingEngine;
    //#endregion

    //#region Private Properties
    private activeAcquiring: boolean;
    private reservedFrontEndKey: string;
    private reservedCaptureDeviceKey: string;
    private soundMeasurementKey: string;
    private videoMeasurementKey: string;
    private queuePromise: Promise<void>;

    private defaultRefreshInterval = 1000;
    private refreshInterval: number = this.defaultRefreshInterval;
    //#endregion

    //#region Backing Fields
    private _lowBandwidthMode: boolean;
    private _useReferenceMicrophone: boolean;
    private _activeSetup: ConnectedMeasurementDeviceSetup;
    private _channelSelection: ChannelSelection;
    private _isReserved: Status = Status.createNotAvailable();
    private _isAcquiring: Status = Status.createNotAvailable();
    private _measurementHealthIssues: MeasurementHealthIssue[] = [];
    //#endregion

    //#region IMeasurementAcquisitionService Properties
    @computedFrom("_lowBandwidthMode")
    public get lowBandwidthMode(): boolean {
        return this._lowBandwidthMode;
    }

    @computedFrom("_useReferenceMicrophone")
    public get useReferenceMicrophone(): boolean {
        return this._useReferenceMicrophone;
    }

    @computedFrom("_activeSetup")
    public get activeSetup(): ConnectedMeasurementDeviceSetup {
        return this._activeSetup;
    }

    @computedFrom("_isReserved")
    public get isReserved(): Status {
        return this._isReserved;
    }

    @computedFrom("_isAcquiring")
    public get isAcquiring(): Status {
        return this._isAcquiring;
    }

    @computedFrom("_isAcquiring")
    private get isAcquiringUserFriendly(): Status {
        // Do not propagate the reason here
        return new Status(this._isAcquiring.statusCode);
    }

    @computedFrom("_isAcquiring", "_measurementHealthIssues")
    public get issues(): MeasurementHealthIssue[] {
        if (this._isAcquiring) {
            return this._measurementHealthIssues;
        } else {
            return [];
        }
    }
    //#endregion

    //#region IMeasurementPreviewService Properties
    @computedFrom("streamMeasurementService.isStreamMeasurementActive", "isAcquiringUserFriendly", "_channelSelection")
    public get isHologramAvailable(): Status {
        if (
            !this._activeSetup ||
            !this._activeSetup.channelSelection ||
            !this.streamMeasurementService.isStreamMeasurementActive
        )
            return Status.createNotAvailable();

        const uraProperties = this._activeSetup.channelSelection.getURAProperties(this._activeSetup.sensorPositions);
        if (!uraProperties.isUra)
            return Status.createNotAvailable("Channel selection is not a Rectangular Uniform Array (URA)");

        return this.streamMeasurementService.isStreamMeasurementActive
            ? Status.createAvailable()
            : this.isAcquiringUserFriendly;
    }

    @computedFrom("streamMeasurementService.isStreamMeasurementActive", "isAcquiringUserFriendly")
    public get isBeamformingProjectionAvailable(): Status {
        return this.streamMeasurementService.isStreamMeasurementActive
            ? Status.createAvailable()
            : this.isAcquiringUserFriendly;
    }

    @computedFrom("streamMeasurementService.isStreamMeasurementActive", "isAcquiringUserFriendly")
    public get isSpectrumAvailable(): Status {
        return this.streamMeasurementService.isStreamMeasurementActive
            ? Status.createAvailable()
            : this.isAcquiringUserFriendly;
    }

    @computedFrom("reservedCaptureDeviceKey", "_isReserved", "captureDeviceService.isRefreshing")
    public get isVideoAvailable(): Status {
        if (this.reservedCaptureDeviceKey) {
            return Status.createAvailable();
        }

        if (this.captureDeviceService.isRefreshing) {
            return Status.createInProgress("Discovering..");
        }

        if (this._isReserved.inProgress && this._activeSetup && this._activeSetup.captureDevices.length > 0) {
            return Status.createInProgress("Connecting..");
        }

        return Status.createNotAvailable("Video stream is not available");
    }

    //#endregion

    constructor(
        frontEndService: IReservableHardwareService<FrontEnd, string>,
        captureDeviceService: IReservableHardwareService<CaptureDevice, string>,
        streamMeasurementService: StreamMeasurementService,
        soundStreamPreviewService: SoundStreamPreviewService,
        videoStreamPreviewService: VideoStreamPreviewService,
        storageService: IStorageService,
        channelSelectionService: IChannelSelectionPresetService,
        notificationService: INotificationService,
        bindingEngine: BindingEngine
    ) {
        this.frontEndService = frontEndService;
        this.captureDeviceService = captureDeviceService;
        this.streamMeasurementService = streamMeasurementService;
        this.soundStreamPreviewService = soundStreamPreviewService;
        this.videoStreamPreviewService = videoStreamPreviewService;
        this.storageService = storageService;
        this.channelSelectionService = channelSelectionService;
        this.queuePromise = Promise.resolve();
        this.notificationService = notificationService;
        this.bindingEngine = bindingEngine;

        window.onbeforeunload = this.beforeUnload;

        // The following line is technically a memory leak, however since services have singleton lifetime the cleanup should take place on unload of the page, which will happen anyway:
        this.bindingEngine
            .collectionObserver(this.streamMeasurementService.measurementState)
            .subscribe(this.measurementStateChanged);
    }

    //#region IMeasurementAcquisitionService Methods

    public setActiveSetup(setup: ConnectedMeasurementDeviceSetup): void {
        if (ConnectedMeasurementDeviceSetup.equals(this._activeSetup, setup)) {
            // Setup is already active, so do nothing.
            return;
        }

        // First make sure acquisition is stopped, will append to queuePromise internally
        this._stopAcquisition();

        // Set the selected setup
        this._activeSetup = setup;

        // Reset interval
        this.refreshInterval = this.defaultRefreshInterval;

        if (this._activeSetup) {
            // Make sure every Measurement Device has a valid channel selection
            this._activeSetup.forEach((info) => {
                if (
                    !info.channelSelection ||
                    info.channelSelection.count != info.measurementDevice.frontEnd.microphoneCount
                ) {
                    info.channelSelection = this.channelSelectionService.getDefaultPreset(
                        info.measurementDevice.frontEnd
                    );
                }
            });
        }

        this.queuePromise = this.queuePromise
            .then(() => this.releaseSetup())
            .then(() => {
                // Tell the listeners that we're connecting to the device
                if (!this._isReserved.inProgress || this._isReserved.reason)
                    this._isReserved = Status.createInProgress();
            })
            .then(() => this.reserveSetup(setup));

        // Restart acquisition, will append to queuePromise internally
        this._startAcquisition();
    }

    public setChannelSelections(newChannelSelections: {
        [frontEndId: string]: ChannelSelection | ChannelSelectionUsage;
    }): Promise<void> {
        if (!this._activeSetup) throw new Error("No measurement setup is currently active.");

        const selectedChannelSelections: { [frontEndId: string]: ChannelSelection } = {};
        for (const frontEndId in newChannelSelections) {
            if (!Object.prototype.hasOwnProperty.call(newChannelSelections, frontEndId)) continue;

            const channelSelection = newChannelSelections[frontEndId];

            const measurementDeviceInfo = this._activeSetup.find((x) => x.measurementDevice.frontEnd.id === frontEndId);

            if (!measurementDeviceInfo)
                throw new Error("FrontEnd with id " + frontEndId + " is not present in the active measurement setup.");

            let newChannelSelection: ChannelSelection;

            // Update selection
            if (channelSelection instanceof ChannelSelection) {
                newChannelSelection = channelSelection;
            } else {
                newChannelSelection = this.channelSelectionService.getDefaultPreset(
                    measurementDeviceInfo.measurementDevice.frontEnd,
                    channelSelection
                );
            }

            // The same, do nothing
            if (
                !measurementDeviceInfo.channelSelection ||
                !newChannelSelection ||
                newChannelSelection.id !== measurementDeviceInfo.channelSelection.id
            ) {
                selectedChannelSelections[frontEndId] = newChannelSelection;
            }
        }

        // Stop acquisition
        const acquiring = this.isAcquiring.available;
        this._stopAcquisition();

        // Update channel selection on the acquisition promise to make sure we don't get in some bugged state
        return (this.queuePromise = this.queuePromise.then(() => {
            for (const frontEndId in selectedChannelSelections) {
                if (Object.prototype.hasOwnProperty.call(selectedChannelSelections, frontEndId)) {
                    this._activeSetup.find((x) => x.measurementDevice.frontEnd.id === frontEndId).channelSelection =
                        selectedChannelSelections[frontEndId];
                }
            }

            // Continue acquisition
            if (acquiring) this._startAcquisition();
        }));
    }

    public setLowBandwidthMode(lowBandwidthMode: boolean): void {
        // Stop acquisition
        const acquiring = this.isAcquiring.available;
        this._stopAcquisition();

        // Update channel selection on the acquisition promise to make sure we don't get in some bugged state
        this.queuePromise = this.queuePromise.then(() => {
            this._lowBandwidthMode = lowBandwidthMode;

            // Continue acquisition
            if (acquiring) this._startAcquisition();
        });
    }

    public setUseReferenceMicrophone(useReferenceMicrophone: boolean): void {
        // Stop acquisition
        const acquiring = this.isAcquiring.available;
        this._stopAcquisition();

        // Update reference microphone selection on the acquisition promise to make sure we don't get in some bugged state
        this.queuePromise = this.queuePromise.then(() => {
            this._useReferenceMicrophone = useReferenceMicrophone;

            // Continue acquisition
            if (acquiring) this._startAcquisition();
        });
    }

    public startAcquisition(): Promise<void> {
        // Set internal state
        this.activeAcquiring = true;

        if (!this._isReserved.available) return Promise.reject(this._isReserved.reason);

        // Fire internal start call
        return this._startAcquisition();
    }

    public stopAcquisition(): Promise<void> {
        // Set internal state
        this.activeAcquiring = false;

        // Fire internal stop call
        return this._stopAcquisition();
    }

    public record(
        duration: number,
        containerId?: string,
        soundProgressCallback?: (progress: number) => void,
        videoProgressCallback?: (progress: number) => void
    ): Promise<string> {
        return this.queuePromise.then(() => {
            if (!this._isAcquiring.statusCode)
                return Promise.reject<string>(new Error("No stream measurement is active"));

            // Create a storage for the recording
            const storageIdPromise = containerId
                ? Promise.resolve(containerId)
                : this.storageService.create(StorageTypes.recording);

            return storageIdPromise.then((storageId) => {
                return this.streamMeasurementService
                    .record(storageId, 0, duration, soundProgressCallback)
                    .then(() => storageId);
            });
        });
    }
    //#endregion

    //#region IMeasurementPreviewService Methods
    public previewHologram(properties: StreamHologramProperties): Promise<StreamHologram> {
        if (this.soundMeasurementKey) {
            return this.soundStreamPreviewService.previewHologram(this.soundMeasurementKey, properties);
        } else {
            return Promise.reject<StreamHologram>(new Error("There is no active measurement"));
        }
    }

    public previewBeamformingProjection(
        properties: StreamBeamformingProjectionProperties
    ): Promise<StreamBeamformingProjection> {
        if (this.soundMeasurementKey) {
            return this.soundStreamPreviewService.previewBeamformingProjection(this.soundMeasurementKey, properties);
        } else {
            return Promise.reject<StreamBeamformingProjection>(new Error("There is no active measurement"));
        }
    }

    public previewSpectrum(StreamSpectrumProperties: StreamSpectrumProperties): Promise<StreamSpectrum> {
        if (this.soundMeasurementKey) {
            return this.soundStreamPreviewService.previewSpectrum(this.soundMeasurementKey, StreamSpectrumProperties);
        } else {
            return Promise.reject<StreamSpectrum>(new Error("There is no active measurement"));
        }
    }

    public getResolution(): Promise<Resolution> {
        if (this.reservedCaptureDeviceKey) {
            return this.videoStreamPreviewService.getResolution(this.reservedCaptureDeviceKey);
        } else {
            return Promise.reject<Resolution>(new Error("There is no active capture device"));
        }
    }

    public previewImage(): Promise<string> {
        if (this.reservedCaptureDeviceKey) {
            return this.videoStreamPreviewService.previewImage(this.reservedCaptureDeviceKey);
        } else {
            return Promise.reject<string>(new Error("There is no active capture device"));
        }
    }

    public getStreamAddress(): Promise<string> {
        if (this.reservedCaptureDeviceKey) {
            return this.videoStreamPreviewService.getStreamAddress(this.reservedCaptureDeviceKey);
        } else {
            return Promise.reject<string>(new Error("There is no active capture device"));
        }
    }

    public getVisibleRegion(distance: number): Promise<Region> {
        return this.videoStreamPreviewService.getVisibleRegion(distance, this.reservedCaptureDeviceKey);
    }
    //#endregion

    //#region IHardwareReserver<string> Methods

    public onDeviceLost(device: string): void {
        this._stopAcquisition();

        const dev = this._activeSetup;
        this.setActiveSetup(null);

        // Reset active device
        this._isReserved = Status.createNotAvailable("Device was lost");

        if (dev) {
            this._isReserved = Status.createInProgress("Device was lost");
            this.setActiveSetup(dev);
        }
    }

    //#endregion

    //#region Callbacks

    private beforeUnload = (): boolean => {
        // Check if we are measuring
        if (this.isAcquiring && this.isAcquiring.available) {
            // We cannot do anything after the confirm call so just let the measurement timeout..
            return confirm("A measurement is active, are you sure you want to proceed?");
        }

        // Unregister device
        this.setActiveSetup(undefined);
    };

    private measurementStateChanged = () => {
        // Create a collection of measurement health issues
        this._measurementHealthIssues = this.streamMeasurementService.measurementState
            .map((x) => {
                if (x.state && x.state.issues) {
                    return x.state.issues;
                }

                return [];
            })
            .reduce((a, b) => a.concat(b), []);

        if (!this._isAcquiring || this._measurementHealthIssues.length == 0) return;

        // Register notifications for each of them IF a measurement is active
        const message = this.convertHealthIssuesToNotificationString(this._measurementHealthIssues);
        const source = this.convertHealthIssuesToNotificationTitle(this._measurementHealthIssues);

        // Check if notification already exists
        const index = this.notificationService.notifications.findIndex(
            (x) => x.source == source && x.message == message
        );

        // if not, register
        if (index < 0) {
            this.notificationService.registerProgressNotification(source, message, NotificationUrgency.Warning);
        }
    };

    //#endregion

    //#region Helper Methods

    // Reserves the devices in the given measurement setup for use
    private reserveSetup(setup: ConnectedMeasurementDeviceSetup): Promise<void> {
        if (!setup) {
            this._isReserved = Status.createNotAvailable();
            return;
        }

        if (this._activeSetup !== setup) return;

        const promises: Promise<void>[] = [];

        if (setup.frontEnds.length !== 0) {
            promises.push(this.frontEndService.canReserve(setup.frontEnds));

            promises.push(
                this.frontEndService
                    .reserve(
                        setup.map((x) => x.measurementDevice.frontEnd),
                        this,
                        setup.map((x) => x.position)
                    )
                    .then((key) => {
                        this.reservedFrontEndKey = key;
                    })
            );
        }

        if (setup.captureDevices.length !== 0) {
            const captureDevicesWithPosition = setup.reduce(
                (prev, curr) =>
                    prev.concat(
                        curr.measurementDevice.captureDevices.map(
                            (x) => <PositionedCaptureDevice>{ captureDevice: x, position: curr.position }
                        )
                    ),
                <PositionedCaptureDevice[]>[]
            );

            // Reserve multiple devices once that is supported. In the meanwhile, a setup should not have more then 1 device
            const captureDeviceWithPosition = captureDevicesWithPosition[0];

            promises.push(
                this.captureDeviceService
                    .reserve([captureDeviceWithPosition.captureDevice], this, [captureDeviceWithPosition.position])
                    .then((key) => {
                        this.reservedCaptureDeviceKey = key;
                    })
            );
        }

        // Wait for all promises to resolve, then throw any error that might have occurred
        return PromiseExtensions.allIncludingErrored(promises)
            .then(() => {
                // Update status
                this._isReserved = Status.createAvailable();
            })
            .catch((e: Error[]) => {
                return this.releaseSetup().then(() => {
                    // Device has changed, stop ...
                    if (this._activeSetup !== setup) return;

                    this._isReserved = Status.createInProgress(e.find((x) => x != undefined).message);

                    this.refreshInterval *= 2;

                    // Retry after a few seconds, to prevent spamming
                    return PromiseExtensions.delayedPromise(this.refreshInterval).then(() => this.reserveSetup(setup));
                });
            });
    }

    // Release all devices
    private releaseSetup(): Promise<void> {
        // If no devices are reserved just return
        if (!this.reservedFrontEndKey && !this.reservedCaptureDeviceKey) return Promise.resolve();

        const promises: Promise<void>[] = [];
        if (this.reservedFrontEndKey) {
            // Release front end
            promises.push(
                this.frontEndService
                    .release(this.reservedFrontEndKey, this)
                    .catch(() => {})
                    .then(() => {
                        this.reservedFrontEndKey = undefined;
                    })
            );
        }

        if (this.reservedCaptureDeviceKey) {
            // Release capture device
            promises.push(
                this.captureDeviceService
                    .release(this.reservedCaptureDeviceKey, this)
                    .catch(() => {})
                    .then(() => {
                        this.reservedCaptureDeviceKey = undefined;
                    })
            );
        }

        return Promise.all(promises).then(() => {
            this._isReserved = Status.createNotAvailable();
        });
    }

    private _startAcquisition = () => {
        // Append new acquisition
        return (this.queuePromise = this.queuePromise
            .then(() => this.stopMeasurement())
            .then(() => {
                if (!this._activeSetup || this._activeSetup.length == 0) return Promise.resolve();

                return this.startMeasurement(
                    <AudioStreamProperties>{
                        deviceKey: this.reservedFrontEndKey,
                        channelSelection: this._activeSetup.channelSelection.getSelection(),
                        orderMode:
                            this._activeSetup.length > 1 ? OrderMode.Sampler : OrderMode.LeftToRightThenTopToBottom,
                        fixedPointFraction: 31,
                        decimationNormalisationFactor: 28.2843 / 14.0,
                        sampleRate: 46875,
                        storageSize: 2000,
                        windowSize: 1875,
                        lowBandwidthMode: this._lowBandwidthMode,
                        useReferenceMicrophone: this._useReferenceMicrophone,
                    },
                    !this.reservedCaptureDeviceKey
                        ? undefined
                        : <VideoStreamProperties>{
                              deviceKey: this.reservedCaptureDeviceKey,
                          }
                );
            }));
    };

    private _stopAcquisition = () => {
        // Append stop
        return (this.queuePromise = this.queuePromise.then(() => this.stopMeasurement()));
    };

    // Start a new measurement using the provided settings
    private startMeasurement(soundProps: AudioStreamProperties, videoProps?: VideoStreamProperties): Promise<void> {
        if (!this.activeAcquiring) return;

        this._isAcquiring = Status.createInProgress("Setting up measurement");

        const props: (AudioStreamProperties | VideoStreamProperties)[] = [soundProps];
        if (videoProps) {
            props.push(videoProps);
        }

        return this.streamMeasurementService
            .create(props, (e) => this.onMeasurementError(e))
            .then((keys) => {
                this.soundMeasurementKey = keys[0];
                if (keys.length > 1) {
                    this.videoMeasurementKey = keys[0];
                }
            })
            .then(() => {
                this._isAcquiring = Status.createAvailable();
            })
            .catch((error: Error) => {
                console.error(error);

                // Stop all measurements
                return this.stopMeasurement()
                    .catch(() => {})
                    .then(() => {
                        this._isAcquiring = Status.createNotAvailable(
                            error.message ? error.message : "An unknown error occurred"
                        );
                    });
            });
    }

    // Stop measurement
    private stopMeasurement(): Promise<void> {
        if (!this.soundMeasurementKey && !this.videoMeasurementKey) return Promise.resolve();

        return this.streamMeasurementService
            .cancel()
            .catch(() => {})
            .then(() => {
                this.soundMeasurementKey = undefined;
                this.videoMeasurementKey = undefined;
                this._isAcquiring = Status.createNotAvailable();
            });
    }

    private onMeasurementError(error: StreamMeasurementServiceError): void {
        this._isAcquiring = Status.createNotAvailable("An error ocurred while measuring: " + error.message);
        if (this.streamMeasurementService.isStreamMeasurementActive) {
            this.stopMeasurement();
        }
    }

    private convertHealthIssuesToNotificationString(issues: MeasurementHealthIssue[]): string {
        if (issues.length === 1) {
            return issues[0].description;
        }

        if (issues.length > 1) {
            return "Live streaming might not behave as expected";
        }
        return "";
    }

    private convertHealthIssuesToNotificationTitle(issues: MeasurementHealthIssue[]): string {
        if (issues.length == 1) {
            if (issues[0].type === MeasurementHealthIssueType.HighCPULoad) {
                return "High CPU load";
            }

            if (issues[0].type === MeasurementHealthIssueType.LowOnMemory) {
                return "Low on memory";
            }
        }
        return "Slow SoundCamera Connection";
    }
}
//#endregion
